import * as React from "react";
import { ChevronDown, MapPin, Video } from "lucide-react";

import { Input } from "@/components/ui/input";
import { VideoOption, VideoState, conferenceProviderLabel } from "@/lib/calendarProviders";
import { cn } from "@/lib/utils";

/**
 * States the host can act on. Each takes them to the calendar settings, and their in-progress
 * page is autosaved with a crumb waiting to bring them back.
 */
const ACTION: Partial<Record<VideoState, string>> = {
  connect: "Connect a calendar →",
  switch: "Switch calendar →",
  reconnect: "Reconnect →",
};

/** States they cannot act on from here, named so the row isn't just greyed out unexplained. */
const NOTE: Partial<Record<VideoState, string>> = {
  account: " (not on this account)",
  unavailable: " (not enabled here)",
  soon: " (coming soon)",
};

/**
 * One control for the whole meeting location, allowing ONE OF EACH type at once: a video call
 * (a per-booking link) AND/OR a physical place — a meeting can be both (e.g. a room with a
 * dial-in). A custom dropdown rather than a native `<select>`, so it can hold two independent
 * choices and show the real Video / MapPin (map-balloon) icons per type, consistently.
 *
 * Which video providers exist, and whether each is usable, is the caller's business: this
 * renders whatever it is handed, so a provider gained or lost never needs a change here.
 */
export function MeetingLocationPicker({
  provider,
  onProviderChange,
  physicalOn,
  onPhysicalChange,
  location,
  onLocationChange,
  videoOptions,
  onConnect,
}: {
  provider: string;
  onProviderChange: (v: string) => void;
  physicalOn: boolean;
  onPhysicalChange: (v: boolean) => void;
  location: string;
  onLocationChange: (v: string) => void;
  /** The video providers to offer, in display order. Null while they are still being read. */
  videoOptions: VideoOption[] | null;
  /** Take the host to their calendar settings (the draft is autosaved and waiting on return). */
  onConnect: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const chips: React.ReactNode[] = [];
  if (provider) {
    chips.push(
      <span key="video" className="inline-flex items-center gap-1">
        <Video className="h-3.5 w-3.5 text-brand-blue" /> {conferenceProviderLabel(provider)}
      </span>,
    );
  }
  if (physicalOn) {
    chips.push(
      <span key="place" className="inline-flex items-center gap-1">
        <MapPin className="h-3.5 w-3.5 text-brand-coral" /> {location.trim() || "Physical location"}
      </span>,
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-left text-sm"
      >
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-foreground">
          {chips.length === 0 ? (
            <span className="text-muted-foreground">Choose meeting locations</span>
          ) : (
            chips.map((c, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="text-muted-foreground/50">·</span>}
                {c}
              </React.Fragment>
            ))
          )}
        </span>
        <ChevronDown
          className={cn("h-4 w-4 shrink-0 text-muted-foreground transition", open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-border bg-background p-2 shadow-lg">
          <div className="mb-1 flex items-center gap-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-brand-blue">
            <Video className="h-3.5 w-3.5" /> Video call
          </div>
          <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-foreground hover:bg-muted">
            <input
              type="radio"
              name="mlp-video"
              checked={!provider}
              onChange={() => onProviderChange("")}
            />
            None
          </label>
          {videoOptions === null ? (
            // Not known yet. Saying nothing beats claiming a working calendar isn't enabled.
            <div className="px-2 py-1 text-sm text-muted-foreground">Loading…</div>
          ) : (
            videoOptions.map((o) => {
              const label = conferenceProviderLabel(o.value);
              const action = ACTION[o.state];

              // Something the host can fix, from here, in one click.
              if (action) {
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onConnect();
                    }}
                    className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-sm hover:bg-muted"
                  >
                    <span className="flex items-center gap-2 text-foreground">
                      <Video className="h-3.5 w-3.5 text-brand-blue" /> {label}
                    </span>
                    <span className="shrink-0 text-xs font-semibold text-brand-blue">{action}</span>
                  </button>
                );
              }

              // Only a calendar that can mint right now is selectable.
              const disabled = o.state !== "ready";
              return (
                <label
                  key={o.value}
                  className={cn(
                    "flex items-center gap-2 rounded px-2 py-1 text-sm",
                    disabled
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer text-foreground hover:bg-muted",
                  )}
                >
                  <input
                    type="radio"
                    name="mlp-video"
                    disabled={disabled}
                    checked={provider === o.value}
                    onChange={() => onProviderChange(o.value)}
                  />
                  {label}
                  {NOTE[o.state] ?? ""}
                </label>
              );
            })
          )}

          <div className="my-1.5 border-t border-border" />

          {/* Checkbox sits right next to the balloon + 'In person'; ticking it reveals the field. */}
          <label className="flex cursor-pointer items-center gap-2 px-1 py-1 text-xs font-semibold uppercase tracking-wide text-brand-coral">
            <input
              type="checkbox"
              checked={physicalOn}
              onChange={(e) => onPhysicalChange(e.target.checked)}
            />
            <MapPin className="h-3.5 w-3.5" /> In person
          </label>
          {physicalOn && (
            <Input
              autoFocus
              value={location}
              maxLength={500}
              placeholder="e.g. Room 3, or an address"
              onChange={(e) => onLocationChange(e.target.value)}
              onKeyDown={(e) => {
                // Enter registers the typed location (it's already live via onChange) and closes
                // the picker, so the host doesn't have to click away.
                if (e.key === "Enter") {
                  e.preventDefault();
                  setOpen(false);
                }
              }}
              className="mt-1"
            />
          )}
        </div>
      )}
    </div>
  );
}
